import React, { useEffect, useRef, useState } from "react";
import { MotifIcon, MotifTruncate } from "@ey-xd/motif-react";
import { IconoirNavArrowUp, IconoirNavArrowDown } from "@ey-xd/motif-icon";
import { getDateStringShortMonth } from "../../../utils/dateFunction";
import "./ConsumptionTable.scss";
import {
  getCostIntervalForCostMetric,
  getDurationType,
} from "../../Checkout/CheckoutHelper";

const ConsumptionTable = ({
  consumptionListData,
  setConsumptionListData,
  isSummary,
  activeTab,
}: {
  consumptionListData: any[];
  setConsumptionListData: React.Dispatch<React.SetStateAction<any[]>>;
  isSummary: boolean;
  activeTab: number;
}) => {
  const tableRefs = useRef<Record<string, HTMLTableElement | null>>({});
  const scrollRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const fixedHeaderRefs = useRef<Record<string, HTMLTableElement | null>>({});

  const [fixedHeaderByProduct, setFixedHeaderByProduct] = useState<
    Record<string, boolean>
  >({});
  const [columnWidthsByProduct, setColumnWidthsByProduct] = useState<
    Record<string, number[]>
  >({});
  const [containerWidthByProduct, setContainerWidthByProduct] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    const handleScrollOrResize = () => {
      consumptionListData.forEach(({ ProductId }) => {
        const tableEl = tableRefs.current[ProductId];
        const scrollEl = scrollRefs.current[ProductId];
        if (!tableEl || !scrollEl) return;

        const tableTop = tableEl.getBoundingClientRect().top;

        setFixedHeaderByProduct((prev) => ({
          ...prev,
          [ProductId]: tableTop < 0,
        }));

        const width = scrollEl.getBoundingClientRect().width;
        setContainerWidthByProduct((prev) => ({
          ...prev,
          [ProductId]: width,
        }));
      });
    };

    handleScrollOrResize();

    window.addEventListener("scroll", handleScrollOrResize);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [consumptionListData]);

  useEffect(() => {
    consumptionListData.forEach(({ ProductId }) => {
      const tableEl = tableRefs.current[ProductId];
      if (!tableEl) return;

      const firstRow = tableEl.querySelector("thead tr");
      if (!firstRow) return;

      const widths: number[] = Array.from(firstRow.children).map(
        (cell) => (cell as HTMLElement).getBoundingClientRect().width,
      );

      setColumnWidthsByProduct((prev) => ({
        ...prev,
        [ProductId]: widths,
      }));
    });
  }, [consumptionListData]);

  const syncScroll = (productId: string) => () => {
    const scrollEl = scrollRefs.current[productId];
    const fixedHeaderEl = fixedHeaderRefs.current[productId];
    if (!scrollEl || !fixedHeaderEl) return;

    const scrollLeft = scrollEl.scrollLeft;
    fixedHeaderEl.style.transform = `translateX(-${scrollLeft}px)`;
  };

  const handleProductToggleExpand = (productId: string) => {
    setConsumptionListData((prev: any[]) =>
      prev.map((product: any) =>
        product.ProductId === productId
          ? { ...product, isExpanded: !product.isExpanded }
          : product,
      ),
    );
  };

  const handleSkuToggleExpand = (productId: string, skuId: string) => {
    setConsumptionListData((prev: any[]) =>
      prev.map((product: any) =>
        product.ProductId === productId
          ? {
              ...product,
              SkuDetails: product.SkuDetails.map((sku: any) =>
                sku.SkuId === skuId
                  ? { ...sku, isExpanded: !sku.isExpanded }
                  : { ...sku, isExpanded: false },
              ),
            }
          : product,
      ),
    );
  };

  const handleUnitChange = (value: string, TransactionId: number) => {
    const updatedData = consumptionListData.map((product: any) => ({
      ...product,
      SkuDetails: product.SkuDetails.map((sku: any) => ({
        ...sku,
        Transactions: sku.Transactions.map((txn: any) => {
          if (txn.TransactionId === TransactionId) {
            const previousValue = String(txn.Quantity ?? "");
            const sanitizedValue = value.replace(/[^0-9.]/g, "");
            const decimalIndex = sanitizedValue.indexOf(".");
            const integerPartRaw =
              decimalIndex === -1
                ? sanitizedValue
                : sanitizedValue.slice(0, decimalIndex);
            const decimalPartRaw =
              decimalIndex === -1
                ? ""
                : sanitizedValue.slice(decimalIndex + 1).replace(/\./g, "");

            const integerPart = integerPartRaw.slice(0, 6);
            const decimalPart = decimalPartRaw.slice(0, 1);

            let val =
              decimalIndex !== -1
                ? `${integerPart}.${decimalPart}`
                : integerPart;

            if (val === ".") {
              val = "";
            }

            const numericVal = Number(val);
            const isInvalidNumber = val !== "" && Number.isNaN(numericVal);
            const exceedsMaxValue =
              !isInvalidNumber && val !== "" && numericVal > 999999;

            if (isInvalidNumber || exceedsMaxValue) {
              val = previousValue;
            }

            return {
              ...txn,
              Quantity: val,
            };
          }
          return txn;
        }),
      })),
    }));
    setConsumptionListData(updatedData);
  };

  function calculateTransactionCost(txn: any) {
    const units = txn.Quantity || 0;
    const cost = txn.CostMetricCost || 0;
    const totalCost = units * cost;
    if (txn.Quantity !== "" && !isNaN(units) && !isNaN(cost)) {
      return totalCost.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return "--";
  }

  function calculateTotalCost(transactions: any[]) {
    const total = transactions.reduce((sum, txn) => {
      const units = txn.Quantity || 0;
      const cost = txn.CostMetricCost || 0;
      if (txn.Quantity !== "" && !isNaN(units) && !isNaN(cost)) {
        return sum + units * cost;
      }
      return sum;
    }, 0);
    return total.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return (
    <div className="consumption-list-wrapper">
      {consumptionListData.map((consumptionInfo: any) => {
        const pid = consumptionInfo.ProductId;
        const fixedHeader = fixedHeaderByProduct[pid];
        const colWidths = columnWidthsByProduct[pid] ?? [];
        const totalWidth = colWidths.length
          ? colWidths.reduce((a, b) => a + b, 0)
          : undefined;
        const containerWidth = containerWidthByProduct[pid];

        return (
          <div className="consumption-list" key={consumptionInfo.ProductId}>
            <div
              className="consumption-list-header pointer"
              onClick={() =>
                handleProductToggleExpand(consumptionInfo.ProductId)
              }
            >
              <div>
                <h2 className="motif-body1-default-regular">
                  {consumptionInfo.ProductName}
                </h2>
              </div>
              <div className="consumption-list-header-btn pointer">
                <MotifIcon
                  iconFunction={
                    consumptionInfo.isExpanded
                      ? IconoirNavArrowUp
                      : IconoirNavArrowDown
                  }
                  size="24"
                />
              </div>
            </div>
            {consumptionInfo.isExpanded && (
              <>
                {consumptionInfo.SkuDetails.map((sku: any) => {
                  return (
                    <React.Fragment key={sku.SkuId}>
                      {/* Content goes here */}
                      <div className="consumption-table-wrapper">
                        <div
                          className={`sku-table-info pointer`}
                          onClick={() =>
                            handleSkuToggleExpand(
                              consumptionInfo.ProductId,
                              sku.SkuId,
                            )
                          }
                        >
                          <div>
                            <div className="sku-name-text">{sku.Name}</div>
                            <div className="sku-number-text">{sku.SkuId}</div>
                          </div>
                          <div className={`total-cost-grid`}>
                            <div className={`total-cost`}>
                              {`Total cost: ${calculateTotalCost(sku.Transactions)}`}
                            </div>
                            <div className="pointer space-18-left">
                              <MotifIcon
                                iconFunction={
                                  sku.isExpanded
                                    ? IconoirNavArrowUp
                                    : IconoirNavArrowDown
                                }
                                size="24"
                              />
                            </div>
                          </div>
                        </div>
                        {sku.isExpanded && (
                          <div
                            className="consumption-table-container"
                            style={{ position: "relative" }}
                          >
                            {fixedHeader && (
                              <div
                                className="fixed-header-wrapper"
                                style={{
                                  overflow: "hidden",
                                  width:
                                    totalWidth && totalWidth > containerWidth
                                      ? containerWidth
                                      : totalWidth,
                                }}
                              >
                                <table
                                  ref={(el) => {
                                    fixedHeaderRefs.current[pid] = el;
                                  }}
                                  className="motif-table-standard fixed-header"
                                  style={{
                                    width: totalWidth ?? "auto",
                                    willChange: "transform",
                                  }}
                                >
                                  <thead>
                                    <tr>
                                      <th
                                        style={{ width: colWidths[0] }}
                                        className="motif-caption-default-regular usage-period"
                                      >
                                        Usage period
                                      </th>
                                      <th
                                        style={{ width: colWidths[1] }}
                                        className="motif-caption-default-regular orderId"
                                      >
                                        Order name/ID
                                      </th>
                                      <th
                                        style={{ width: colWidths[2] }}
                                        className="motif-caption-default-regular engagementCode"
                                      >
                                        Engagement code/name
                                      </th>
                                      <th
                                        style={{ width: colWidths[3] }}
                                        className="motif-caption-default-regular duration"
                                      >
                                        No. of periods
                                      </th>
                                      <th
                                        style={{ width: colWidths[4] }}
                                        className="motif-caption-default-regular cost-metric right-aligned"
                                      >
                                        Unit cost
                                      </th>
                                      <th
                                        style={{ width: colWidths[5] }}
                                        className="motif-caption-default-regular unit-cost"
                                      >
                                        Quantity used
                                      </th>
                                      <th
                                        style={{ width: colWidths[6] }}
                                        className="motif-caption-default-regular quantity right-aligned"
                                      >
                                        Total cost
                                      </th>
                                    </tr>
                                  </thead>
                                </table>
                              </div>
                            )}
                            <div
                              className="table-scroll-wrapper"
                              ref={(el) => {
                                scrollRefs.current[pid] = el;
                              }}
                              onScroll={syncScroll(pid)}
                            >
                              <table
                                className="motif-table-standard"
                                ref={(el) => {
                                  tableRefs.current[pid] = el;
                                }}
                              >
                                <thead>
                                  <tr>
                                    <th
                                      style={{ width: colWidths[0] }}
                                      className="motif-caption-default-regular usage-period"
                                    >
                                      Usage period
                                    </th>
                                    <th
                                      style={{ width: colWidths[1] }}
                                      className="motif-caption-default-regular orderId"
                                    >
                                      Order name/ID
                                    </th>
                                    <th
                                      style={{ width: colWidths[2] }}
                                      className="motif-caption-default-regular engagementCode"
                                    >
                                      Engagement code/name
                                    </th>
                                    <th
                                      style={{ width: colWidths[3] }}
                                      className="motif-caption-default-regular duration"
                                    >
                                      No. of periods
                                    </th>
                                    <th
                                      style={{ width: colWidths[4] }}
                                      className="motif-caption-default-regular cost-metric right-aligned"
                                    >
                                      Unit cost
                                    </th>
                                    <th
                                      style={{ width: colWidths[5] }}
                                      className="motif-caption-default-regular unit-cost"
                                    >
                                      Quantity used
                                    </th>
                                    <th
                                      style={{ width: colWidths[6] }}
                                      className="motif-caption-default-regular quantity right-aligned"
                                    >
                                      Total cost
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sku.isExpanded && (
                                    <tr>
                                      <td className="tree-list-cell">
                                        <div className="tree-wrapper">
                                          <ul className="tree-list">
                                            {sku.Transactions.map(
                                              (txn: any) => (
                                                <li
                                                  className="tree-trigger"
                                                  key={txn.TransactionId}
                                                >
                                                  <div className="no-wrap">
                                                    {`${getDateStringShortMonth(
                                                      txn.IntervalStartDtm,
                                                    )} - `}
                                                  </div>
                                                  <div>
                                                    {getDateStringShortMonth(
                                                      txn.IntervalEndDtm,
                                                    )}
                                                  </div>
                                                </li>
                                              ),
                                            )}
                                          </ul>
                                        </div>
                                      </td>
                                      <td>
                                        {sku.Transactions.map((txn: any) => (
                                          <div
                                            className="table-cell"
                                            key={txn.TransactionId}
                                          >
                                            <div>
                                              <MotifTruncate
                                                maxLines={1}
                                                maxWidth={150}
                                              >
                                                {txn.Description}
                                              </MotifTruncate>
                                              <div className="supportive-text">
                                                {txn.OrderId}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </td>
                                      <td>
                                        {sku.Transactions.map((txn: any) => (
                                          <>
                                            <div
                                              className="table-cell"
                                              key={txn.TransactionId}
                                            >
                                              {txn.EngagementId}
                                              <div className="supportive-text">
                                                {txn.EngagementName}
                                              </div>
                                            </div>
                                          </>
                                        ))}
                                      </td>
                                      <td>
                                        {sku.Transactions.map((txn: any) => {
                                          return (
                                            <div
                                              className="table-cell"
                                              key={txn.TransactionId}
                                            >
                                              {`${txn.IntervalNum} of ${txn.Intervals}`}
                                              <div className="supportive-text uppercase">
                                                {getDurationType(
                                                  sku.CostIntervalCd,
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </td>
                                      <td>
                                        {sku.Transactions.map((txn: any) => (
                                          <div
                                            className="table-cell right-aligned"
                                            key={txn.TransactionId}
                                          >
                                            {txn?.CostMetricCost.toLocaleString(
                                              "en-US",
                                              {
                                                style: "currency",
                                                currency: "USD",
                                                maximumFractionDigits: 2,
                                                minimumFractionDigits: 2,
                                              },
                                            )}
                                            <div className="supportive-text uppercase">
                                              {getCostIntervalForCostMetric(
                                                sku.CostIntervalCd,
                                                sku.CostMetricCd,
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </td>
                                      <td>
                                        {sku.Transactions.map((txn: any) =>
                                          isSummary ? (
                                            <div
                                              className="table-cell"
                                              key={txn.TransactionId}
                                            >
                                              {txn.Quantity}
                                            </div>
                                          ) : (
                                            <React.Fragment
                                              key={txn.TransactionId}
                                            >
                                              <input
                                                key={`txn.Quantity.${txn.TransactionId}`}
                                                type="text"
                                                placeholder="Enter value"
                                                className="units-input table-cell"
                                                value={txn.Quantity}
                                                aria-label={"Units"}
                                                disabled={activeTab === 1}
                                                onChange={(
                                                  event: React.ChangeEvent<HTMLInputElement>,
                                                ) => {
                                                  handleUnitChange(
                                                    event.target.value,
                                                    txn.TransactionId,
                                                  );
                                                }}
                                              />
                                            </React.Fragment>
                                          ),
                                        )}
                                      </td>
                                      <td>
                                        {sku.Transactions.map((txn: any) => (
                                          <div
                                            className="table-cell right-aligned"
                                            key={txn.TransactionId}
                                          >
                                            {calculateTransactionCost(txn)}
                                          </div>
                                        ))}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ConsumptionTable;
